/**
 * HTML5 <audio> player synced to the project playhead. Used for
 * file-uploaded audio (data URLs) and direct https:// audio URLs.
 */

import { getState, subscribe } from '../store/project';
import type { AudioTrack, FileAudioTrack } from '../types';

export class HtmlAudioPlayer {
  private el: HTMLAudioElement;
  private currentSrc = '';
  private syncTimer: number | null = null;

  constructor() {
    this.el = document.createElement('audio');
    this.el.preload = 'auto';
    this.el.style.display = 'none';
    document.body.appendChild(this.el);
    subscribe((s) => {
      const tracks = s.project.audioTracks ?? (s.project.audio ? [s.project.audio] : []);
      const file = tracks.find((t) => t.kind === 'file') as FileAudioTrack | undefined;
      this.onStateChange(file);
    });
    this.startSyncLoop();
  }

  private onStateChange(audio: FileAudioTrack | undefined): void {
    if (!audio) {
      if (!this.el.paused) this.el.pause();
      return;
    }
    if (this.currentSrc !== audio.src) {
      this.el.src = audio.src;
      this.currentSrc = audio.src;
    }
  }

  private startSyncLoop(): void {
    if (this.syncTimer) return;
    this.syncTimer = window.setInterval(() => this.tick(), 80);
  }

  private resolveTrack(): FileAudioTrack | undefined {
    const { project } = getState();
    const tracks = project.audioTracks ?? (project.audio ? [project.audio] : []);
    return tracks.find((t: AudioTrack) => t.kind === 'file') as FileAudioTrack | undefined;
  }

  private tick(): void {
    const { project, playhead, isPlaying } = getState();
    const audio = this.resolveTrack();
    if (!audio) return;

    const songSec = (audio.songStart + playhead) / 1000;
    const songEndSec = audio.songEnd / 1000;
    this.el.volume = computeVolume(audio, playhead, project.duration);

    if (isPlaying && songSec < songEndSec) {
      if (Math.abs(this.el.currentTime - songSec) > 0.25) {
        try {
          this.el.currentTime = songSec;
        } catch {
          // ignore seek errors during initial load
        }
      }
      if (this.el.paused) this.el.play().catch(() => {});
    } else {
      if (!this.el.paused) this.el.pause();
      if (!isPlaying && playhead === 0) {
        try {
          this.el.currentTime = audio.songStart / 1000;
        } catch {
          // ignore
        }
      }
    }
  }
}

function computeVolume(audio: FileAudioTrack, playhead: number, duration: number): number {
  let v = audio.volume;
  if (audio.fadeIn && playhead < audio.fadeIn) v *= playhead / audio.fadeIn;
  if (audio.fadeOut && playhead > duration - audio.fadeOut) {
    v *= Math.max(0, (duration - playhead) / audio.fadeOut);
  }
  return Math.max(0, Math.min(1, v));
}
